import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Chip,
    TextField, InputAdornment
} from '@mui/material';
import { Add as AddIcon, Visibility as ViewIcon, Search as SearchIcon, ShoppingCart as PurchaseIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { useNavigate } from 'react-router-dom';

const PurchaseOrdersPage = () => {
    const navigate = useNavigate();
    const [pos, setPos] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const getHeaders = useCallback(() => {
        const userInfo = localStorage.getItem('userInfo');
        const token = userInfo ? JSON.parse(userInfo).token : null;
        return { headers: { Authorization: `Bearer ${token}` } };
    }, []);

    const fetchPOs = useCallback(async () => {
        try {
            const res = await axios.get(getApiUrl('purchases'), getHeaders());
            setPos(res.data);
        } catch (error) {
            console.error('Error fetching POs:', error);
        }
    }, [getHeaders]);

    useEffect(() => {
        fetchPOs();
    }, [fetchPOs]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'RECEIVED': return 'success';
            case 'PARTIALLY_PAID': return 'info';
            case 'PAID': return 'success';
            case 'PENDING': return 'warning';
            case 'CANCELLED': return 'error';
            default: return 'default';
        }
    };

    const filteredPos = pos.filter(po =>
        po.orderNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.supplier?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PurchaseIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                        Purchase Orders
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/dashboard/purchases/create')}
                    sx={{
                        bgcolor: '#ffab00',
                        color: '#1a237e',
                        fontWeight: 'bold',
                        '&:hover': { bgcolor: '#ffc107' }
                    }}
                >
                    Create New Order
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by PO Number or Supplier..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            <Paper sx={{ width: '100%', mb: 2, borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>PO Number</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Amount</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPos.map((po) => (
                                <TableRow key={po.id} hover>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{po.orderNo}</TableCell>
                                    <TableCell>{new Date(po.orderDate).toLocaleDateString()}</TableCell>
                                    <TableCell>{po.supplier?.name}</TableCell>
                                    <TableCell>${Number(po.totalAmount).toFixed(2)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={po.status}
                                            color={getStatusColor(po.status) as any}
                                            size="small"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => navigate(`/dashboard/purchases/${po.id}`)}>
                                            <ViewIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredPos.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        No purchase orders found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default PurchaseOrdersPage;
